import type { InputHTMLAttributes, ReactNode } from 'react'

import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

export interface PackageOption {
    value: string
    label: string
    helper?: string | null
    trailing?: string
    // data-mdlfy-cms paths of the database values shown as label and helper.
    labelCms?: string
    helperCms?: string
}

interface PackageRadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
    options: PackageOption[]
    value?: string
    legend?: ReactNode
    error?: string
}

const PackageRadio = forwardRef<HTMLInputElement, PackageRadioProps>(({ options, legend, error, name, value, onChange, ...rest }, ref) => (
    <fieldset className='flex flex-col gap-2'>
        {legend ? <legend className='text-sm font-medium text-neutral-700'>{legend}</legend> : null}

        <div className='grid gap-3 sm:grid-cols-2'>
            {options.map((option, idx) => {
                const id = `${name}-${option.value}`
                const isChecked = value === option.value

                return (
                    <label
                        key={option.value}
                        htmlFor={id}
                        className={cn(
                            'group relative cursor-pointer rounded-card border-2 bg-white/80 p-4 transition-all',
                            'hover:bg-white',
                            isChecked
                                ? 'border-brand-red shadow-soft'
                                : 'border-transparent shadow-[inset_0_0_0_1px_rgba(0,0,0,0.05)]'
                        )}
                    >
                        <input
                            ref={idx === 0 ? ref : undefined}
                            id={id}
                            type='radio'
                            name={name}
                            value={option.value}
                            checked={isChecked}
                            onChange={onChange}
                            className='peer sr-only'
                            {...rest}
                        />

                        <div className='flex items-center justify-between gap-3'>
                            <div className='flex items-center gap-3'>
                                <span
                                    aria-hidden
                                    className={cn(
                                        'flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors',
                                        isChecked ? 'border-brand-red' : 'border-neutral-300 group-hover:border-neutral-400'
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'block h-2.5 w-2.5 rounded-full bg-brand-red transition-transform',
                                            isChecked ? 'scale-100' : 'scale-0'
                                        )}
                                    />
                                </span>

                                <div>
                                    <div className='font-display font-semibold text-neutral-900' data-mdlfy-cms={option.labelCms}>
                                        {option.label}
                                    </div>

                                    {option.helper ? (
                                        <div className='text-xs text-neutral-500' data-mdlfy-cms={option.helperCms}>
                                            {option.helper}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {option.trailing ? (
                                <div className='font-display font-bold text-brand-red'>{option.trailing}</div>
                            ) : null}
                        </div>
                    </label>
                )
            })}
        </div>

        {error ? <p className='text-xs font-medium text-brand-red'>{error}</p> : null}
    </fieldset>
))

PackageRadio.displayName = 'PackageRadio'

export default PackageRadio
